import { gql, useQuery } from "@apollo/client";
import {
    BarChart3,
    ChevronLeft,
    ChevronRight,
    ExternalLink,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

const VANDERWOOD_STREAM_SESSIONS_QUERY = gql`
    query VanderwoodStreamSessions($limit: Int) {
        vanderwoodStreamSessions(limit: $limit) {
            id
            twitchStreamId
            startedAt
            endedAt
            streamTitle
            gameName
            peakViewerCount
            lastViewerCount
        }
    }
`;

interface VanderwoodStreamSession {
    id: string;
    twitchStreamId: string;
    startedAt: string;
    endedAt: string | null;
    streamTitle: string;
    gameName: string;
    peakViewerCount: number;
    lastViewerCount: number;
}

type RangeFilter = "7d" | "30d" | "90d" | "all";
type MetricMode = "both" | "peak" | "last";
const TABLE_PAGE_SIZE = 15;

export default function VanderwoodStreamsPage() {
    const [rangeFilter, setRangeFilter] = useState<RangeFilter>("30d");
    const [gameFilter, setGameFilter] = useState<string>("all");
    const [minPeakFilter, setMinPeakFilter] = useState<number>(0);
    const [metricMode, setMetricMode] = useState<MetricMode>("both");
    const [tablePage, setTablePage] = useState(1);

    const { data, loading, error } = useQuery<{
        vanderwoodStreamSessions: VanderwoodStreamSession[];
    }>(VANDERWOOD_STREAM_SESSIONS_QUERY, {
        variables: { limit: 100 },
        fetchPolicy: "cache-and-network",
    });

    const sessions = data?.vanderwoodStreamSessions || [];

    const gameOptions = useMemo(() => {
        const uniqueGames = new Set<string>();
        for (const session of sessions) {
            const game = session.gameName?.trim();
            if (game) {
                uniqueGames.add(game);
            }
        }

        return Array.from(uniqueGames).sort((a, b) => a.localeCompare(b));
    }, [sessions]);

    const filteredSessions = useMemo(() => {
        const nowMs = Date.now();
        const rangeToDays: Record<Exclude<RangeFilter, "all">, number> = {
            "7d": 7,
            "30d": 30,
            "90d": 90,
        };

        const cutoffMs =
            rangeFilter === "all"
                ? Number.NEGATIVE_INFINITY
                : nowMs - rangeToDays[rangeFilter] * 24 * 60 * 60 * 1000;

        return sessions.filter((session) => {
            const startedAtMs = new Date(session.startedAt).getTime();
            const matchesRange = startedAtMs >= cutoffMs;
            const matchesGame =
                gameFilter === "all" ||
                (session.gameName || "").trim() === gameFilter;
            const matchesPeak = session.peakViewerCount >= minPeakFilter;
            return matchesRange && matchesGame && matchesPeak;
        });
    }, [sessions, rangeFilter, gameFilter, minPeakFilter]);

    const chartData = useMemo(() => {
        return filteredSessions
            .map((session) => ({
                id: session.id,
                startedAtMs: new Date(session.startedAt).getTime(),
                startedAtLabel: new Date(session.startedAt).toLocaleString([], {
                    month: "short",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                }),
                peakViewerCount: session.peakViewerCount,
                lastViewerCount: session.lastViewerCount,
            }))
            .sort((a, b) => a.startedAtMs - b.startedAtMs);
    }, [filteredSessions]);

    const filteredSessionsForTable = useMemo(() => {
        return [...filteredSessions].sort(
            (a, b) =>
                new Date(b.startedAt).getTime() -
                new Date(a.startedAt).getTime(),
        );
    }, [filteredSessions]);

    const totalTablePages = Math.max(
        1,
        Math.ceil(filteredSessionsForTable.length / TABLE_PAGE_SIZE),
    );

    useEffect(() => {
        setTablePage(1);
    }, [rangeFilter, gameFilter, minPeakFilter]);

    useEffect(() => {
        setTablePage((current) => Math.min(current, totalTablePages));
    }, [totalTablePages]);

    const paginatedSessionsForTable = useMemo(() => {
        const start = (tablePage - 1) * TABLE_PAGE_SIZE;
        const end = start + TABLE_PAGE_SIZE;
        return filteredSessionsForTable.slice(start, end);
    }, [filteredSessionsForTable, tablePage]);

    if (loading) {
        return (
            <div className="mx-auto mt-4 w-full px-4">
                <div className="rounded-2xl border border-slate-700 bg-slate-800 p-6">
                    <p className="text-slate-300">Loading stream history...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mx-auto mt-4 w-full px-4">
                <div className="rounded-2xl border border-red-700 bg-red-900/20 p-6">
                    <p className="text-red-300">
                        Failed to load stream history.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto mt-4 w-full px-4">
            <div className="mb-6 rounded-2xl border border-slate-700 bg-slate-800 p-6">
                <div className="flex items-center gap-3">
                    <BarChart3 className="h-7 w-7 text-yellow-400" />
                    <div>
                        <h1 className="text-3xl font-bold text-slate-100">
                            Vanderwood Stream History
                        </h1>
                        <p className="mt-1 text-sm text-slate-400">
                            Click a stream to view detailed analytics, viewer
                            trends, and linked checkins.
                        </p>
                    </div>
                </div>
            </div>

            <div className="mb-6 rounded-2xl border border-slate-700 bg-slate-900 p-6">
                <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <label className="text-xs font-semibold tracking-wide text-slate-400 uppercase">
                        Time Range
                        <select
                            value={rangeFilter}
                            onChange={(event) =>
                                setRangeFilter(
                                    event.target.value as RangeFilter,
                                )
                            }
                            className="mt-1 block w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200"
                        >
                            <option value="7d">Last 7 days</option>
                            <option value="30d">Last 30 days</option>
                            <option value="90d">Last 90 days</option>
                            <option value="all">All time</option>
                        </select>
                    </label>

                    <label className="text-xs font-semibold tracking-wide text-slate-400 uppercase">
                        Game
                        <select
                            value={gameFilter}
                            onChange={(event) =>
                                setGameFilter(event.target.value)
                            }
                            className="mt-1 block w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200"
                        >
                            <option value="all">All games</option>
                            {gameOptions.map((game) => (
                                <option key={game} value={game}>
                                    {game}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="text-xs font-semibold tracking-wide text-slate-400 uppercase">
                        Min Peak Viewers
                        <input
                            type="number"
                            min={0}
                            step={5}
                            value={minPeakFilter}
                            onChange={(event) =>
                                setMinPeakFilter(
                                    Number.isNaN(Number(event.target.value))
                                        ? 0
                                        : Math.max(
                                              0,
                                              Number(event.target.value),
                                          ),
                                )
                            }
                            className="mt-1 block w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200"
                        />
                    </label>

                    <label className="text-xs font-semibold tracking-wide text-slate-400 uppercase">
                        Metric
                        <select
                            value={metricMode}
                            onChange={(event) =>
                                setMetricMode(event.target.value as MetricMode)
                            }
                            className="mt-1 block w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200"
                        >
                            <option value="both">Peak + Last</option>
                            <option value="peak">Peak only</option>
                            <option value="last">Last only</option>
                        </select>
                    </label>
                </div>

                <div className="mb-4 flex items-center justify-between gap-2">
                    <h2 className="text-xl font-semibold text-slate-100">
                        Viewers Over Time
                    </h2>
                    <p className="text-sm text-slate-400">
                        Showing {filteredSessionsForTable.length} streams
                    </p>
                </div>

                {chartData.length === 0 ? (
                    <p className="text-sm text-slate-400">
                        No streams match these filters.
                    </p>
                ) : (
                    <div className="h-90 w-full">
                        <ResponsiveContainer>
                            <LineChart data={chartData}>
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="#334155"
                                />
                                <XAxis
                                    dataKey="startedAtLabel"
                                    stroke="#94a3b8"
                                    minTickGap={24}
                                />
                                <YAxis stroke="#94a3b8" />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: "#0f172a",
                                        border: "1px solid #334155",
                                        borderRadius: "8px",
                                    }}
                                    labelStyle={{ color: "#cbd5e1" }}
                                />
                                <Legend />
                                {(metricMode === "both" ||
                                    metricMode === "peak") && (
                                    <Line
                                        type="monotone"
                                        dataKey="peakViewerCount"
                                        name="Peak Viewers"
                                        stroke="#facc15"
                                        strokeWidth={2.5}
                                        dot={false}
                                        activeDot={{ r: 5 }}
                                    />
                                )}
                                {(metricMode === "both" ||
                                    metricMode === "last") && (
                                    <Line
                                        type="monotone"
                                        dataKey="lastViewerCount"
                                        name="Last Viewers"
                                        stroke="#38bdf8"
                                        strokeWidth={2.5}
                                        dot={false}
                                        activeDot={{ r: 5 }}
                                    />
                                )}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-700 bg-slate-900 shadow-lg">
                <table className="w-full">
                    <thead className="border-b border-slate-700 bg-slate-800">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-slate-300 uppercase">
                                Start
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-slate-300 uppercase">
                                End
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-slate-300 uppercase">
                                Title
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-slate-300 uppercase">
                                Game
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-semibold tracking-wider text-slate-300 uppercase">
                                Peak
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-semibold tracking-wider text-slate-300 uppercase">
                                Last
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-semibold tracking-wider text-slate-300 uppercase">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                        {filteredSessionsForTable.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={7}
                                    className="px-4 py-8 text-center text-slate-400"
                                >
                                    No stream history for the current filters.
                                </td>
                            </tr>
                        ) : (
                            paginatedSessionsForTable.map((session) => (
                                <tr
                                    key={session.id}
                                    className="hover:bg-slate-800/50"
                                >
                                    <td className="px-4 py-3 text-sm text-slate-200">
                                        {new Date(
                                            session.startedAt,
                                        ).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-300">
                                        {session.endedAt
                                            ? new Date(
                                                  session.endedAt,
                                              ).toLocaleString()
                                            : "LIVE"}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-200">
                                        {session.streamTitle ||
                                            "Untitled Stream"}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-400">
                                        {session.gameName || "-"}
                                    </td>
                                    <td className="px-4 py-3 text-right text-sm font-semibold text-slate-200">
                                        {session.peakViewerCount.toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-right text-sm text-slate-300">
                                        {session.lastViewerCount.toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <Link
                                            to={`/longhouse/vanderwood-streams/${session.id}`}
                                            className="inline-flex items-center gap-1 rounded-md border border-slate-600 px-3 py-1.5 text-xs font-medium text-slate-200 transition-colors hover:bg-slate-700"
                                        >
                                            <ExternalLink className="h-3.5 w-3.5" />
                                            View
                                        </Link>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                <div className="flex items-center justify-between border-t border-slate-700 px-6 py-4 text-sm text-slate-300">
                    <span>
                        Page {tablePage} of {totalTablePages}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() =>
                                setTablePage((current) =>
                                    Math.max(1, current - 1),
                                )
                            }
                            disabled={tablePage <= 1}
                            className="rounded-lg border border-slate-600 px-3 py-1.5 transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() =>
                                setTablePage((current) =>
                                    Math.min(totalTablePages, current + 1),
                                )
                            }
                            disabled={tablePage >= totalTablePages}
                            className="rounded-lg border border-slate-600 px-3 py-1.5 transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
